import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  InputAdornment,
  Fade,
  Slide,
  alpha,
  styled,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import TimerIcon from '@mui/icons-material/Timer'
import { Package } from 'tabler-icons-react'
import {
  createPlan,
  getPlans,
  updatePlan,
  deletePlan,
} from '../../api/plan/index'
import Swal from 'sweetalert2'

const ACCENT = '#10b981'

// Styled Components for Enhanced UI
const StyledCard = styled(Card)(({ theme, isSpecial }) => ({
  position: 'relative',
  height: '100%',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${alpha(theme.palette.primary.main, isSpecial ? 0.4 : 0.15)}`,
  borderRadius: 16,
  overflow: 'hidden',
  backdropFilter: 'blur(20px)',
  background: isSpecial
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(
        theme.palette.primary.main,
        0.04
      )} 100%)`
    : alpha('#fff', 0.03),
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: alpha(theme.palette.primary.main, isSpecial ? 0.6 : 0.35),
    boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, isSpecial ? 0.25 : 0.15)}`,
  },
  '&::before': isSpecial ? {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  } : {},
}))

const PriceTag = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  marginBottom: theme.spacing(2),
  '& .currency': {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginRight: 4,
  },
  '& .amount': {
    fontSize: '3rem',
    fontWeight: 800,
    color: theme.palette.text.primary,
    letterSpacing: -1,
  },
  '& .period': {
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginLeft: 8,
  },
}))

const FeatureItem = styled(Box)(({ theme, enabled }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1),
  borderRadius: 8,
  backgroundColor: enabled ? alpha(theme.palette.success.main, 0.08) : alpha(theme.palette.grey[500], 0.05),
  transition: 'all 0.2s',
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1.5),
    fontSize: '1.2rem',
    color: enabled ? theme.palette.success.main : theme.palette.grey[400],
  },
  '& .feature-text': {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: enabled ? theme.palette.text.primary : theme.palette.text.secondary,
  },
}))

const initialFormState = {
  id: null,
  name: '',
  price: 0,
  durationDays: 30,
  isActive: true,
  isSpecial: false,
  features: {
    sslMonitoring: false,
    sslExpireMonitoring: false,
    domainExpireMonitoring: false,
    cronJobMonitoring: false,
    maintenanceMode: false,
    getRequest: true,
    postRequest: false,
    putRequest: false,
    patchRequest: false,
    deleteRequest: false,
    optionsRequest: false,
    headRequest: true,
    slowResponseAlert: false,
    statusCodeMonitoring: true,
  },
  limits: {
    maxMonitors: 20,
    minIntervalMinutes: 5,
  },
}

const featureLabels = {
  sslMonitoring: 'SSL İzleme',
  sslExpireMonitoring: 'SSL Süre Sonu İzleme',
  domainExpireMonitoring: 'Domain Süre Sonu İzleme',
  cronJobMonitoring: 'Cron Job İzleme',
  maintenanceMode: 'Bakım Modu',
  getRequest: 'GET İstekleri',
  postRequest: 'POST İstekleri',
  putRequest: 'PUT İstekleri',
  patchRequest: 'PATCH İstekleri',
  deleteRequest: 'DELETE İstekleri',
  optionsRequest: 'OPTIONS İstekleri',
  headRequest: 'HEAD İstekleri',
  slowResponseAlert: 'Yavaş Yanıt Uyarısı',
  statusCodeMonitoring: 'Durum Kodu İzleme',
}

const PlanManagementPage = () => {
  const [plans, setPlans] = useState([])
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState(initialFormState)

  const fetchPlans = async () => {
    try {
      const fetchedPlans = await getPlans()
      setPlans(fetchedPlans)
    } catch (error) {
      console.error('Planlar yüklenemedi:', error)
      Swal.fire('Hata', 'Planlar yüklenirken bir hata oluştu.', 'error')
    }
  }

  useEffect(() => {
    fetchPlans()
  }, [])

  const handleOpenModal = (plan = initialFormState) => {
    if (plan.id) {
      // Mevcut planı düzenlerken
      setFormData({
        ...plan,
        features: plan.features || initialFormState.features,
        limits: plan.limits || initialFormState.limits,
      })
    } else {
      // Yeni plan oluştururken
      setFormData(initialFormState)
    }
    setOpen(true)
  }

  const handleCloseModal = () => {
    setOpen(false)
    setFormData(initialFormState)
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleFeatureToggle = (key) => {
    setFormData((prev) => ({
      ...prev,
      features: { ...prev.features, [key]: !prev.features[key] },
    }))
  }

  const handleLimitChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      limits: { ...prev.limits, [key]: parseInt(value) || 0 },
    }))
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      Swal.fire('Uyarı', 'Plan adı boş olamaz.', 'warning')
      return
    }

    // features/limits backend'den id ve planId (Prisma FK) ile geliyor;
    // bunları göndermeden temizle, aksi halde validasyon "id is not allowed" hatası verir.
    const { id: _fId, planId: _fPlanId, ...cleanFeatures } = formData.features || {}
    const { id: _lId, planId: _lPlanId, ...cleanLimits } = formData.limits || {}

    const planData = {
      name: formData.name,
      price: parseInt(formData.price) || 0,
      durationDays: parseInt(formData.durationDays) || 30,
      isActive: formData.isActive,
      isSpecial: formData.isSpecial,
      features: cleanFeatures,
      limits: cleanLimits,
    }

    try {
      if (formData.id) {
        // Güncelleme
        await updatePlan(formData.id, planData)
        Swal.fire('Başarılı', 'Plan başarıyla güncellendi.', 'success')
      } else {
        // Yeni oluşturma
        console.log("form data: ", planData)
        await createPlan(planData)
        Swal.fire('Başarılı', 'Plan başarıyla oluşturuldu.', 'success')
      }
      await fetchPlans()
      handleCloseModal()
    } catch (error) {
      console.error('Plan kaydedilemedi:', error)
      Swal.fire(
        'Hata',
        error.response?.data?.message || 'Plan kaydedilirken bir hata oluştu.',
        'error'
      )
    }
  }

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Emin misiniz?',
      text: 'Bu planı silmek istediğinize emin misiniz?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Evet, sil!',
      cancelButtonText: 'İptal',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deletePlan(id)
          await fetchPlans()
          Swal.fire('Silindi!', 'Plan başarıyla silindi.', 'success')
        } catch (error) {
          console.error('Plan silinemedi:', error)
          Swal.fire(
            'Hata',
            error.response?.data?.message || 'Plan silinirken bir hata oluştu.',
            'error'
          )
        }
      }
    })
  }

  const getEnabledFeatures = (features) => {
    if (!features) return []
    return Object.entries(features)
      .filter(([_, value]) => value === true)
      .map(([key, _]) => key)
  }

  // AdminLayout zaten Container + koyu panel sağlıyor; sayfa kendi kabuğunu kurmuyor.
  return (
    <Box>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
            mb: 4,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 0.5 }}>
              Abonelik Planları
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: alpha('#fff', 0.6) }}>
              Sistemdeki tüm paketleri buradan yönetebilirsiniz
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            sx={{
              bgcolor: ACCENT,
              color: '#000',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            Yeni Plan Oluştur
          </Button>
        </Box>

        {/* Plans Grid */}
        <Grid container spacing={3}>
          {plans.map((plan, index) => (
            <Grid item xs={12} sm={6} md={4} key={plan.id}>
              <Fade in timeout={300 + index * 100}>
                <StyledCard isSpecial={plan.isSpecial}>
                  <CardContent sx={{ p: 3 }}>
                    {/* Header with Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h5" fontWeight="700" gutterBottom>
                          {plan.name}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={plan.isActive ? 'Aktif' : 'Pasif'}
                            color={plan.isActive ? 'success' : 'default'}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          {plan.isSpecial && (
                            <Chip
                              label="Özel"
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenModal(plan)}
                          sx={{
                            bgcolor: alpha(ACCENT, 0.12),
                            '&:hover': { bgcolor: alpha(ACCENT, 0.22) },
                          }}
                        >
                          <EditIcon fontSize="small" sx={{ color: ACCENT }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(plan.id)}
                          sx={{
                            bgcolor: alpha('#d32f2f', 0.1),
                            '&:hover': { bgcolor: alpha('#d32f2f', 0.2) },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>

                    {/* Price */}
                    <PriceTag>
                      <span className="currency">₺</span>
                      <span className="amount">{(plan.price / 100).toFixed(0)}</span>
                      <span className="period">/ {plan.durationDays} gün</span>
                    </PriceTag>

                    {/* Limits */}
                    <Box
                      sx={{
                        bgcolor: alpha('#000', 0.03),
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                      }}
                    >
                      <Stack direction="row" spacing={3}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <MonitorHeartIcon sx={{ fontSize: '1rem', mr: 0.5, color: 'primary.main' }} />
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Maksimum Monitor
                            </Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="800">
                            {plan.limits?.maxMonitors || 0}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <TimerIcon sx={{ fontSize: '1rem', mr: 0.5, color: 'primary.main' }} />
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                              Min. Aralık
                            </Typography>
                          </Box>
                          <Typography variant="h6" fontWeight="800">
                            {plan.limits?.minIntervalMinutes || 0} dk
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Features */}
                    <Typography variant="subtitle2" fontWeight="700" gutterBottom sx={{ mt: 2, mb: 1.5 }}>
                      Özellikler
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflowY: 'auto', pr: 1 }}>
                      {getEnabledFeatures(plan.features).length > 0 ? (
                        getEnabledFeatures(plan.features).map((feature) => (
                          <FeatureItem key={feature} enabled>
                            <CheckCircleIcon />
                            <span className="feature-text">
                              {featureLabels[feature] || feature}
                            </span>
                          </FeatureItem>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2 }}>
                          Özel özellik bulunmuyor
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </StyledCard>
              </Fade>
            </Grid>
          ))}
        </Grid>

        {plans.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              bgcolor: alpha('#fff', 0.02),
              borderRadius: 3,
              border: `2px dashed ${alpha(ACCENT, 0.25)}`,
            }}
          >
            <Package size={48} color={alpha(ACCENT, 0.5)} />
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mt: 2 }}>
              Henüz plan bulunmuyor
            </Typography>
            <Typography sx={{ color: alpha('#fff', 0.6), fontSize: '0.875rem', mb: 3 }}>
              Yeni bir plan oluşturarak başlayın
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{
                bgcolor: ACCENT,
                color: '#000',
                fontWeight: 600,
                px: 3,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': { bgcolor: '#059669' },
              }}
            >
              İlk Planı Oluştur
            </Button>
          </Box>
        )}
      </Box>

      {/* Create/Edit Modal */}
      <Dialog
        open={open}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxHeight: '90vh',
            bgcolor: '#0f172a',
            backgroundImage: 'none',
            border: `1px solid ${alpha(ACCENT, 0.2)}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: '1.5rem',
            borderBottom: `1px solid ${alpha(ACCENT, 0.15)}`,
            pb: 2,
          }}
        >
          {formData.id ? 'Planı Düzenle' : 'Yeni Plan Oluştur'}
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" fontWeight="700" color="primary" gutterBottom>
                Temel Bilgiler
              </Typography>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Plan Adı"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Örn: Başlangıç Paketi"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Fiyat (Kuruş)"
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₺</InputAdornment>
                        ),
                      }}
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      helperText="Kuruş cinsinden giriniz (örn: 9900 = 99₺)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Süre (Gün)"
                      type="number"
                      value={formData.durationDays}
                      onChange={(e) => handleInputChange('durationDays', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isActive}
                          onChange={(e) => handleInputChange('isActive', e.target.checked)}
                          color="success"
                        />
                      }
                      label={
                        <Typography fontWeight={600}>
                          Plan Aktif {formData.isActive ? '✓' : '✗'}
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isSpecial}
                          onChange={(e) => handleInputChange('isSpecial', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography fontWeight={600}>
                          Özel Plan {formData.isSpecial ? '★' : '☆'}
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* Limits */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" fontWeight="700" color="primary" gutterBottom>
                Limitler
              </Typography>
              <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Maksimum Monitor Sayısı"
                    type="number"
                    value={formData.limits.maxMonitors}
                    onChange={(e) => handleLimitChange('maxMonitors', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MonitorHeartIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Minimum Aralık (Dakika)"
                    type="number"
                    value={formData.limits.minIntervalMinutes}
                    onChange={(e) => handleLimitChange('minIntervalMinutes', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TimerIcon color="primary" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Card>
            </Grid>

            {/* Features */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" fontWeight="700" color="primary" gutterBottom>
                Özellikler
              </Typography>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  maxHeight: 300,
                  overflowY: 'auto',
                }}
              >
                <Stack spacing={1}>
                  {Object.keys(formData.features).map((key) => (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        borderRadius: 1,
                        bgcolor: formData.features[key] ? alpha('#4caf50', 0.08) : 'transparent',
                        '&:hover': { bgcolor: alpha('#000', 0.03) },
                        transition: 'all 0.2s',
                      }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        {featureLabels[key] || key}
                      </Typography>
                      <Switch
                        size="small"
                        checked={formData.features[key]}
                        onChange={() => handleFeatureToggle(key)}
                        color="success"
                      />
                    </Box>
                  ))}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(ACCENT, 0.15)}` }}>
          <Button
            onClick={handleCloseModal}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 4,
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            {formData.id ? 'Değişiklikleri Kaydet' : 'Planı Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PlanManagementPage